/**
 * Unified Email Preview Modal Component
 * Used throughout the application for consistent email previews
 */

import React from 'react';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import { Eye } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';

export function EmailPreviewModal({ email, isOpen, onClose }) {
  const { t } = useTranslation();

  if (!email) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-medium flex items-center gap-2">
            <Eye className="h-5 w-5 text-green-600" />
            {t('communications.emailPreview')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Email Template Preview */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(`
                  <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                    <div style="background-color: #2563eb; color: white; padding: 20px; text-align: center;">
                      <h1 style="margin: 0;">Coláiste Lurgan</h1>
                    </div>
                    <div style="padding: 20px; background-color: #f9fafb;">
                      <h2 style="color: #1f2937; margin-top: 0;">${email.subject || 'No Subject'}</h2>
                      <div style="color: #374151; line-height: 1.6;">
                        ${email.content || email.body_preview || t('communications.noContentPreview')}
                      </div>
                    </div>
                    <div style="background-color: #64748b; color: white; padding: 15px; text-align: center; font-size: 12px;">
                      Contact us: info@colaistelurgan.ie | Phone: +353 123 456 789
                    </div>
                  </div>
                `)
              }}
            />
          </div>

          {/* Preview Note */}
          <div className="p-3 bg-blue-50 rounded-lg">
            <div className="flex items-start gap-2">
              <Eye className="h-4 w-4 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">{t('email.previewNote')}</p>
                {((email.content && email.content.length >= 500) ||
                  (email.body_preview && email.body_preview.length >= 500)) && (
                  <p className="mt-1 text-xs italic">
                    {t('communications.contentTruncated')}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="destructive" onClick={onClose}>
            {t('common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EmailPreviewModal;